import type { OrgPendingTimeEntry } from "../org/hooks/usePendingTimeEntries";
import { formatWageType } from "./ad-hoc-time-entry-schema";
import { formatWage } from "./custom-wage";

export const CUSTOM_WAGE_CHOICE_KEY = "custom";
export const OVERTIME_LABEL_SUFFIX = "overtime";
const LABEL_SEPARATOR = " · ";

export type StandardWageOption =
  OrgPendingTimeEntry["standardWageOptions"][number];

export interface StandardWageChoice {
  key: string;
  label: string;
  option: StandardWageOption;
}

type EntryWageSelection = Pick<
  OrgPendingTimeEntry,
  "baseWageType" | "isOvertime" | "customWage"
>;

function getStandardChoiceKey({
  baseWageType,
  isOvertime,
}: Pick<StandardWageOption, "baseWageType" | "isOvertime">): string {
  return `${baseWageType}:${isOvertime ? "overtime" : "base"}`;
}

/** "Regular overtime · $150.00/hr" */
export function formatStandardWageOption(option: StandardWageOption): string {
  let typeLabel = formatWageType(option.baseWageType);
  if (option.isOvertime) {
    typeLabel = `${typeLabel} ${OVERTIME_LABEL_SUFFIX}`;
  }
  return `${typeLabel}${LABEL_SEPARATOR}${formatWage(option)}`;
}

/** One choice per standard option, in the order the server returned them. */
export function buildStandardWageChoices(
  options: StandardWageOption[],
): StandardWageChoice[] {
  return options.map((option) => ({
    key: getStandardChoiceKey(option),
    label: formatStandardWageOption(option),
    option,
  }));
}

/**
 * A custom wage overrides the standard wages, so it wins. Otherwise pick the
 * option matching the entry's wage type and overtime. Null when the entry has
 * no payable wage, so the reviewer must choose.
 */
export function getInitialWageChoiceKey({
  entry,
  choices,
}: {
  entry: EntryWageSelection;
  choices: StandardWageChoice[];
}): string | null {
  if (entry.customWage) {
    return CUSTOM_WAGE_CHOICE_KEY;
  }
  const entryKey = getStandardChoiceKey(entry);
  const match = choices.find((choice) => choice.key === entryKey);
  return match ? match.key : null;
}
